<template>
  <div class="kanban-board">
    <!-- Hero Section -->
    <section class="relative py-32 bg-gradient-to-br from-[#0f4640] to-[#1a6c64] overflow-hidden">
      <!-- Decorative Elements -->
      <div class="absolute inset-0 opacity-10">
        <div class="absolute top-0 left-0 w-96 h-96 bg-white rounded-full filter blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
        <div class="absolute bottom-0 right-0 w-96 h-96 bg-[#faf6df] rounded-full filter blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
      </div>
      
      <!-- Pattern Overlay -->
      <div class="absolute inset-0 opacity-5" style="background-image: url('data:image/svg+xml,%3Csvg width=\'20\' height=\'20\' viewBox=\'0 0 20 20\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\' fill-rule=\'evenodd\'%3E%3Ccircle cx=\'3\' cy=\'3\' r=\'3\'/%3E%3Ccircle cx=\'13\' cy=\'13\' r=\'3\'/%3E%3C/g%3E%3C/svg%3E');"></div>

      <div class="container mx-auto px-4 relative">
        <div class="flex flex-col items-center text-center max-w-4xl mx-auto">
          <h1 class="text-6xl font-bold text-white mb-8 animate-fade-in">
            {{ $t('workspace.kanban.title') }}
            <span class="block text-[#faf6df] text-lg mt-4">{{ $t('workspace.kanban.description') }}</span>
          </h1>
        </div>
      </div>

      <!-- Wave Decoration -->
      <div class="absolute bottom-0 left-0 right-0">
        <svg class="w-full h-12 fill-white" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
        </svg>
      </div>
    </section>

    <!-- Features Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <!-- New Board -->
          <div class="feature-card bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
            <div class="w-14 h-14 bg-[#0f4640] rounded-lg flex items-center justify-center mb-4">
              <i class="fas fa-plus text-[#faf6df] text-2xl"></i>
            </div>
            <h3 class="text-xl font-bold text-[#0f4640] mb-3">{{ $t('workspace.kanban.features.newBoard.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">
              {{ $t('workspace.kanban.features.newBoard.description') }}
            </p>
          </div>

          <!-- Cards Management -->
          <div class="feature-card bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
            <div class="w-14 h-14 bg-[#0f4640] rounded-lg flex items-center justify-center mb-4">
              <i class="fas fa-tasks text-[#faf6df] text-2xl"></i>
            </div>
            <h3 class="text-xl font-bold text-[#0f4640] mb-3">{{ $t('workspace.kanban.features.cards.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">
              {{ $t('workspace.kanban.features.cards.description') }}
            </p>
          </div>

          <!-- Columns Management -->
          <div class="feature-card bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
            <div class="w-14 h-14 bg-[#0f4640] rounded-lg flex items-center justify-center mb-4">
              <i class="fas fa-columns text-[#faf6df] text-2xl"></i>
            </div>
            <h3 class="text-xl font-bold text-[#0f4640] mb-3">{{ $t('workspace.kanban.features.columns.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">
              {{ $t('workspace.kanban.features.columns.description') }}
            </p>
          </div>

          <!-- Filters -->
          <div class="feature-card bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
            <div class="w-14 h-14 bg-[#0f4640] rounded-lg flex items-center justify-center mb-4">
              <i class="fas fa-filter text-[#faf6df] text-2xl"></i>
            </div>
            <h3 class="text-xl font-bold text-[#0f4640] mb-3">{{ $t('workspace.kanban.features.filters.title') }}</h3>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">
              {{ $t('workspace.kanban.features.filters.description') }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Demo Board Section -->
    <section class="py-16 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="flex justify-between items-center mb-8">
          <div class="flex gap-4">
            <button class="px-4 py-2 bg-[#0f4640] text-[#faf6df] rounded-lg hover:bg-opacity-90 transition-all flex items-center gap-2">
              <i class="fas fa-plus"></i>
              {{ $t('workspace.kanban.demo.addColumn') }}
            </button>
            <button class="px-4 py-2 border-2 border-[#0f4640] text-[#0f4640] rounded-lg hover:bg-[#0f4640] hover:text-[#faf6df] transition-all flex items-center gap-2">
              <i class="fas fa-filter"></i>
              {{ $t('workspace.kanban.demo.saveFilter') }}
            </button>
          </div>
          <button class="px-4 py-2 bg-[#0f4640] text-[#faf6df] rounded-lg hover:bg-opacity-90 transition-all flex items-center gap-2">
            <i class="fas fa-plus"></i>
            {{ $t('workspace.kanban.demo.addTask') }}
          </button>
        </div>

        <!-- Kanban Board -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <!-- To Do Column -->
          <div class="bg-white rounded-xl p-4 shadow-lg">
            <h3 class="text-lg font-bold text-[#0f4640] mb-4 flex items-center gap-2">
              <i class="fas fa-list"></i>
              {{ $t('workspace.kanban.demo.columns.todo.title') }}
            </h3>
            <div class="space-y-4">
              <div class="bg-gray-50 p-4 rounded-lg">
                <h4 class="font-bold text-[#0f4640]">{{ $t('workspace.kanban.demo.columns.todo.tasks.task1.title') }}</h4>
                <p class="text-gray-600 text-sm">{{ $t('workspace.kanban.demo.columns.todo.tasks.task1.description') }}</p>
              </div>
              <div class="bg-gray-50 p-4 rounded-lg">
                <h4 class="font-bold text-[#0f4640]">{{ $t('workspace.kanban.demo.columns.todo.tasks.task2.title') }}</h4>
                <p class="text-gray-600 text-sm">{{ $t('workspace.kanban.demo.columns.todo.tasks.task2.description') }}</p>
              </div>
            </div>
          </div>

          <!-- In Progress Column -->
          <div class="bg-white rounded-xl p-4 shadow-lg">
            <h3 class="text-lg font-bold text-[#0f4640] mb-4 flex items-center gap-2">
              <i class="fas fa-spinner"></i>
              {{ $t('workspace.kanban.demo.columns.inProgress.title') }}
            </h3>
            <div class="space-y-4">
              <div class="bg-gray-50 p-4 rounded-lg">
                <h4 class="font-bold text-[#0f4640]">{{ $t('workspace.kanban.demo.columns.inProgress.tasks.task1.title') }}</h4>
                <p class="text-gray-600 text-sm">{{ $t('workspace.kanban.demo.columns.inProgress.tasks.task1.description') }}</p>
              </div>
            </div>
          </div>

          <!-- Done Column -->
          <div class="bg-white rounded-xl p-4 shadow-lg">
            <h3 class="text-lg font-bold text-[#0f4640] mb-4 flex items-center gap-2">
              <i class="fas fa-check"></i>
              {{ $t('workspace.kanban.demo.columns.done.title') }}
            </h3>
            <div class="space-y-4">
              <div class="bg-gray-50 p-4 rounded-lg">
                <h4 class="font-bold text-[#0f4640]">{{ $t('workspace.kanban.demo.columns.done.tasks.task1.title') }}</h4>
                <p class="text-gray-600 text-sm">{{ $t('workspace.kanban.demo.columns.done.tasks.task1.description') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'KanbanBoardView',
  metaInfo() {
    return {
      title: this.$i18n.locale === 'ar' ? 'لوحة كانبان' : 'Kanban Board'
    }
  }
}
</script>

<style scoped>
.kanban-board {
  min-height: 100vh;
  overflow-x: hidden;
}

.feature-card {
  transition: all 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-5px);
}

/* Add animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fadeIn 0.6s ease-out forwards;
}
</style> 